import { Marker } from 'react-map-gl/maplibre';
import { AlertTriangle, Flame, Cross, Shield } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import type { Mission, VehicleType } from '@/state/types';

/**
 * Pulsing speech-bubble style mission markers.
 * - open: blue circle, white card with red AlertTriangle, soft pulse halo.
 * - dispatched/onscene: icon switches based on inferred mission category.
 * - urgent (close to expiry): orange circle with intensified pulse.
 */
export function MissionMarkers() {
  // Phase 13.11 — Mission markers are always visible. The toolbar's bell icon
  // opens the MainPanel directly instead of toggling marker visibility.
  const missions = useGameStore((s) => s.missions);
  const selectDetail = useGameStore((s) => s.selectDetail);
  const gameTime = useGameStore((s) => s.gameTime);

  const active = missions.filter(
    (m) => m.status === 'open' || m.status === 'dispatched' || m.status === 'onscene',
  );

  // Phase 7.3.3 — show a 1s pulse-burst within 3s of last escalation.
  const PULSE_BURST_MS = 1_000;
  const RECENT_ESC_MS = 3_000;

  return (
    <>
      {active.map((m) => {
        const variant = variantFor(m, gameTime);
        const Icon = iconFor(m);
        const styles = STYLES[variant];
        const justEscalated =
          m.lastEscalatedAt !== undefined && gameTime - m.lastEscalatedAt < RECENT_ESC_MS;
        return (
          <Marker
            key={m.id}
            longitude={m.position.lng}
            latitude={m.position.lat}
            anchor="bottom"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                selectDetail({ kind: 'mission', id: m.id });
              }}
              title={m.title}
              aria-label={`Einsatz: ${m.title}`}
              className="relative flex h-13 w-13 items-center justify-center"
              style={{ width: 52, height: 52 }}
            >
              {/* Phase 7.3.3 — escalation burst (large outward pulse) */}
              {justEscalated && (
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: '#f97316',
                    opacity: 0.5,
                    animation: `escalation-burst ${PULSE_BURST_MS}ms ease-out 1`,
                  }}
                />
              )}
              {/* Pulse halo */}
              <span
                aria-hidden
                className={['absolute inset-0 rounded-full', styles.haloAnim].join(' ')}
                style={{ background: styles.haloColor, opacity: 0.4 }}
              />
              {/* Outer ring */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full shadow-xl ring-2 ring-white/80"
                style={{ background: styles.ring }}
              />
              {/* Phase 10.5.4 — Organisation accent ring */}
              <span
                aria-hidden
                className="absolute inset-1 rounded-full"
                style={{
                  border: `2px solid ${organizationBorder(m)}`,
                  background: 'transparent',
                }}
              />
              {/* Inner card */}
              <span
                aria-hidden
                className="relative flex h-9 w-9 items-center justify-center rounded-md bg-white shadow-md"
              >
                <Icon className={['h-5 w-5', styles.iconColor].join(' ')} aria-hidden />
              </span>
            </button>
          </Marker>
        );
      })}
    </>
  );
}

// Phase 8.6.4 — Farblich getrennte Mission-Marker:
//   open       → blau   (#2563eb)
//   dispatched → lila   (#a855f7)
//   onscene    → gelb   (#facc15)
//   urgent     → orange (#f97316) — escalated or near expiry
type Variant = 'open' | 'dispatched' | 'onscene' | 'urgent';

const STYLES: Record<
  Variant,
  { ring: string; haloColor: string; haloAnim: string; iconColor: string }
> = {
  open: {
    ring: '#2563eb',
    haloColor: '#3b82f6',
    haloAnim: 'animate-ping',
    iconColor: 'text-accent-emergency',
  },
  dispatched: {
    ring: '#a855f7', // purple-500
    haloColor: '#c084fc',
    haloAnim: 'animate-pulse',
    iconColor: 'text-accent-emergency',
  },
  onscene: {
    ring: '#facc15', // yellow-400
    haloColor: '#fde047',
    haloAnim: 'animate-pulse',
    iconColor: 'text-accent-emergency',
  },
  urgent: {
    ring: '#f97316', // orange-500
    haloColor: '#f97316',
    haloAnim: 'animate-ping',
    iconColor: 'text-accent-emergency',
  },
};

function variantFor(m: Mission, now: number): Variant {
  // Phase 6.1 — escalated missions are visually urgent regardless of timer
  if (m.escalationCount > 0) return 'urgent';
  // Urgent threshold: < 25 % of remaining lifetime
  const total = m.expiresAt - m.spawnedAt;
  const remaining = m.expiresAt - now;
  if (total > 0 && remaining > 0 && remaining / total < 0.25) return 'urgent';
  // Phase 8.6.4 — distinct colors per status
  if (m.status === 'onscene') return 'onscene';
  if (m.status === 'dispatched') return 'dispatched';
  return 'open';
}

const FIRE_TYPES = new Set<VehicleType>([
  'ELW', 'MTW', 'DLK', 'PKW', 'TLF', 'HLF', 'LF', 'GW', 'RW', 'SW', 'TSF',
]);

function iconFor(m: Mission): LucideIcon {
  // Phase 10.5.4 — Police missions get a shield icon.
  if (m.category === 'police') return Shield;
  if (m.status === 'open') return AlertTriangle;
  // Inspect required types — fire-bias vs ems-bias
  const fire = m.requiredVehicles.some((r) => FIRE_TYPES.has(r.type));
  return fire ? Flame : Cross;
}

/**
 * Phase 10.5.4 — Border colour by organisation. Cross-org missions (police
 * mission with EMS measures, etc.) get a violet outline.
 */
function organizationBorder(m: Mission): string {
  const cat = m.category;
  // Detect cross-org by inspecting whether this mission has at least one
  // EMS measure on a police mission (or vice versa).
  const hasEms = (m.measures ?? []).some((x) => x.organization === 'ems');
  const hasPolice = (m.measures ?? []).some((x) => x.organization === 'police');
  const hasFire = (m.measures ?? []).some((x) => x.organization === 'fire');
  const distinct =
    [hasEms, hasPolice, hasFire].filter(Boolean).length;
  if (distinct >= 2) return '#a855f7'; // violet — cross-org
  if (cat === 'police') return '#1d4ed8'; // blue
  if (cat === 'rescue') return '#f97316'; // orange — Rettung
  return '#dc2626'; // red — Brand/Technisch
}
